import { Box, InputAdornment, Typography } from "@mui/material";
import { MuiFileInput } from "mui-file-input";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useTranslation } from "react-i18next";
import { SectionTitle } from "../Common/SectionTitle/SectionTitle";
import { Loader } from "../Loader/Loader";
import { AttachmentContainer, Container, Footer, StyledButton } from "./styles";
import { useRef, useState } from "react";
import { BlockedFileExtensions, MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } from "../../Types/constants";

interface DropzoneProps {
  files: File[];
  setFiles: (files: File[]) => void;
  onFileUpload: () => void;
  width?: string;
  loading?: boolean;
}

export const Dropzone = ({ files, setFiles, onFileUpload, width, loading }: DropzoneProps) => {
  const { t } = useTranslation();
  const newFilesTitle = t('attachments.new_files.title');
  const [isDragging, setIsDragging] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dropzoneRef = useRef<HTMLDivElement>(null);

  const handleAttachFiles = () => {
    onFileUpload();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const validateFiles = (newFiles: File[]) => {
    for (const file of newFiles) {
      const fileName = file.name;
      const extension = fileName.split('.').pop()?.toLowerCase();

      if (extension && BlockedFileExtensions.includes(extension)) {
        setErrorMessage(
          t("attachments.error_file_extension_blocked", { extension })
        );
        return false;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        setErrorMessage(
          t("attachments.error_file_too_large", { filename: fileName, size: MAX_FILE_SIZE_MB })
        );
        return false;
      }
    }

    setErrorMessage(null);
    return true;
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newFiles = Array.from(e.dataTransfer.files);
      if (validateFiles(newFiles)) {
        setFiles([...files, ...newFiles]);
      }
      e.dataTransfer.clearData();
    }
  };

  const handleFileChange = (newFiles: File[]) => {
    if (validateFiles(newFiles)) {
      setFiles(newFiles);
    }
  };

  return (
    <Container width={width}>
      <SectionTitle title={newFilesTitle} />
      <AttachmentContainer>
        {loading && <Loader isLoading={true} />}

        <Box
          ref={dropzoneRef}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          sx={{
            border: `1px solid ${isDragging ? "#2196f3" : "#ccc"}`,
            borderRadius: 2,
            padding: 4,
            backgroundColor: isDragging ? "rgba(33, 150, 243, 0.1)" : "white",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            marginTop: 2,
            transition: "all 0.2s ease",
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 40, color: "#2196f3" }} />
          <Typography variant="body2" color="textSecondary" sx={{ marginBottom: 2, textAlign: "center" }}>
            {t("attachments.drag_drop_files")}
          </Typography>

          <MuiFileInput
            value={files}
            onChange={handleFileChange}
            placeholder="Insert a file"
            multiple
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AttachFileIcon />
                </InputAdornment>
              ),
            }}
          />

          {errorMessage && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Typography>
          )}

          {files.length > 0 && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t("attachments.selected_files")}:
              </Typography>
              {files.map((file, index) => (
                <Typography key={index} variant="body2" sx={{ color: 'text.secondary' }}>
                  {file.name} ({(file.size / 1024).toFixed(2)} KB)
                </Typography>
              ))}
            </Box>
          )}
        </Box>

        <Footer>
          <StyledButton variant="outlined" onClick={() => {
            setFiles([]);
            setErrorMessage(null);
          }}
          >
            {t("attachments.cancel")}
          </StyledButton>
          <StyledButton
            disabled={files.length === 0 || loading || !!errorMessage}
            variant="contained"
            onClick={handleAttachFiles}
          >
            {t("attachments.confirm")}
          </StyledButton>
        </Footer>
      </AttachmentContainer>
    </Container >
  );
};